import { appKey } from '@app/shared/brand'
import { useSyncExternalStore } from 'react'

/**
 * Whether this browser reads its own fonts for a page.
 *
 * Held on the device and never against the account: the fonts are on this
 * machine, so another machine answers for itself. Unset reads wherever the
 * browser already holds the permission; `off` reads nothing even then, which
 * is the one thing the browser's own permission cannot say.
 */
export type LocalFontSetting = 'on' | 'off' | null

const KEY = appKey('setting', 'local-fonts')
const listeners = new Set<() => void>()

function read(): LocalFontSetting {
  try {
    const stored = localStorage.getItem(KEY)
    return stored === 'on' || stored === 'off' ? stored : null
  } catch {
    return null
  }
}

let current: LocalFontSetting | undefined

function snapshot(): LocalFontSetting {
  if (current === undefined) current = read()
  return current
}

export function setLocalFontSetting(next: Exclude<LocalFontSetting, null>) {
  current = next
  try {
    localStorage.setItem(KEY, next)
  } catch {
    /* A device that refuses to remember still honors the choice until reload. */
  }
  for (const notify of listeners) notify()
}

function listen(notify: () => void): () => void {
  listeners.add(notify)
  return () => {
    listeners.delete(notify)
  }
}

export function useLocalFontSetting(): LocalFontSetting {
  return useSyncExternalStore(listen, snapshot)
}
